import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CalendarMonth as CalendarIcon,
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ClientPaymentService from '../../services/clientPayment.service';
import { useTranslation } from '../../hooks/useTranslation';

const statusColors = {
  Active: 'success',
  suspended: 'warning',
  ended: 'error',
};

const ClientPaymentDetail = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await ClientPaymentService.get(id);
      setData(response.data?.data || response.data);
    } catch (error) {
      showSnackbar(t('clientPayments.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    if (!data) return;
    const newStatus = data.status === 'Active' ? 'suspended' : 'Active';
    try {
      await ClientPaymentService.update(id, { status: newStatus });
      showSnackbar(
        newStatus === 'Active'
          ? t('clientPayments.reactivatedSuccess')
          : t('clientPayments.suspendedSuccess')
      );
      fetchDetail();
    } catch {
      showSnackbar(t('clientPayments.errorUpdating'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">{t('clientPayments.notFound')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate('/memberships')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" fontWeight={600}>
                {t('clientPayments.membershipDetail')}
              </Typography>
              <Chip
                label={t(`clientPayments.status${data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}`)}
                color={statusColors[data.status] || 'default'}
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          }
          action={
            hasPermission('clientpayments.create') && data.status !== 'ended' ? (
              <Button
                variant="outlined"
                color={data.status === 'Active' ? 'warning' : 'success'}
                startIcon={data.status === 'Active' ? <PauseIcon /> : <PlayIcon />}
                onClick={handleStatusToggle}
                sx={{ mr: 1 }}
              >
                {data.status === 'Active'
                  ? t('clientPayments.suspend')
                  : t('clientPayments.reactivate')}
              </Button>
            ) : null
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('clientPayments.client')}
                  </Typography>
                </Box>
                <Typography variant="h6">{data.client?.name || '—'}</Typography>
                {data.client?.email && (
                  <Typography variant="body2" color="text.secondary">
                    {data.client.email}
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PaymentIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('clientPayments.paymentType')}
                  </Typography>
                </Box>
                <Typography variant="h6">{data.payment_type?.name || '—'}</Typography>
                {data.payment_type && (
                  <Box mt={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('paymentTypes.amount')}: ${data.payment_type.monto}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('paymentTypes.installments')}:{' '}
                      {data.payment_type.indefinite || data.payment_type.cuotas === 0
                        ? t('paymentTypes.indefiniteLabel')
                        : data.payment_type.cuotas}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CalendarIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('clientPayments.dates')}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  <strong>{t('clientPayments.startDate')}:</strong> {formatDate(data.start)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('clientPayments.endDate')}:</strong> {formatDate(data.end_date)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('clientPayments.nextPayment')}:</strong> {formatDate(data.next_payment_date)}
                </Typography>
              </Paper>
            </Grid>

            {(data.sus_start_date || data.sus_end_date) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: 'warning.50' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    {t('clientPayments.suspensionInfo')}
                  </Typography>
                  <Box display="flex" gap={3}>
                    <Typography variant="body2">
                      <strong>{t('clientPayments.susStart')}:</strong> {formatDate(data.sus_start_date)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('clientPayments.susEnd')}:</strong> {formatDate(data.sus_end_date)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {data.descount && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    {t('clientPayments.discount')}
                  </Typography>
                  <Box display="flex" gap={2}>
                    {data.descount.porcentaje && (
                      <Chip label={`${data.descount.porcentaje}%`} color="secondary" variant="outlined" size="small" />
                    )}
                    {data.descount.monto && (
                      <Chip label={`$${data.descount.monto}`} color="secondary" variant="outlined" size="small" />
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientPaymentDetail;
